import html2canvas, { Options } from 'html2canvas';

// Helper canvas for converting any modern CSS color (including oklch, lab, color(...)) to standard rgba(...)
let colorCanvas: HTMLCanvasElement | null = null;
let colorCtx: CanvasRenderingContext2D | null = null;

function getColorContext(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (!colorCanvas) {
    colorCanvas = document.createElement('canvas');
    colorCanvas.width = 1;
    colorCanvas.height = 1;
  }
  if (!colorCtx && colorCanvas) {
    colorCtx = colorCanvas.getContext('2d', { willReadFrequently: true });
  }
  return colorCtx;
}

export function convertColorToStandard(color: string): string {
  if (!color || typeof color !== 'string') return '#000000';
  const trimmed = color.trim();
  if (
    !trimmed.includes('oklch') &&
    !trimmed.includes('color(') &&
    !trimmed.includes('lab(') &&
    !trimmed.includes('hwb(') &&
    !trimmed.includes('lch(')
  ) {
    return trimmed;
  }
  const ctx = getColorContext();
  if (!ctx) return '#1e293b';
  try {
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = '#000000';
    ctx.fillStyle = trimmed;
    ctx.fillRect(0, 0, 1, 1);
    const data = ctx.getImageData(0, 0, 1, 1).data;
    const alpha = (data[3] / 255).toFixed(2);
    return `rgba(${data[0]}, ${data[1]}, ${data[2]}, ${alpha})`;
  } catch {
    return '#1e293b';
  }
}

export interface SafeHtml2CanvasOptions extends Partial<Options> {
  timeout?: number;
  stripStylesheets?: boolean;
}

/**
 * Safe wrapper around html2canvas that:
 * 1. Strips heavy Tailwind v4 stylesheets by default when rendering isolated HTML templates (which use inline CSS),
 *    preventing CSS parser freezing and unsupported color crashes completely.
 * 2. Sets clean fallback colors and fast timeouts.
 */
export async function safeHtml2Canvas(
  element: HTMLElement,
  options: SafeHtml2CanvasOptions = {}
): Promise<HTMLCanvasElement> {
  const originalOnClone = options.onclone;
  const stripStylesheets = options.stripStylesheets !== false; // default true for maximum speed and safety

  const safeOptions: Partial<Options> = {
    backgroundColor: '#ffffff',
    scale: 2,
    logging: false,
    useCORS: true,
    allowTaint: false,
    ...options,
    onclone: (clonedDoc: Document, clonedElement: HTMLElement) => {
      try {
        // Sanitize all images to prevent canvas tainting
        const allImages = Array.from(clonedElement.querySelectorAll('img'));
        allImages.forEach(img => {
          try {
            // Replace external googleusercontent logo with local same-origin /logo.png
            if (img.src && (img.src.includes('googleusercontent.com') || img.src.includes('lh3.google'))) {
              img.src = '/logo.png';
            }
            img.crossOrigin = 'anonymous';
          } catch {
            // ignore
          }
        });

        if (stripStylesheets) {
          // Remove all heavy application stylesheets (Tailwind v4, Google fonts, CSS variables)
          // so html2canvas doesn't freeze attempting to parse complex CSS rules.
          const sheets = Array.from(clonedDoc.querySelectorAll('style, link[rel="stylesheet"]'));
          sheets.forEach(s => s.remove());

          // Add a lightweight base style for standard font & rendering
          const baseStyle = clonedDoc.createElement('style');
          baseStyle.textContent = `
            * { box-sizing: border-box; }
            body, html { margin: 0; padding: 0; font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; background-color: #ffffff; color: #0f172a; direction: rtl; }
          `;
          clonedDoc.head?.appendChild(baseStyle);
        } else {
          // If stylesheets are preserved, sanitize oklch/lab colors
          const styleTags = Array.from(clonedDoc.querySelectorAll('style'));
          styleTags.forEach(style => {
            if (style.textContent && /oklch|lab\(|color\(|hwb\(|lch\(/i.test(style.textContent)) {
              style.textContent = style.textContent
                .replace(/oklch\([^)]+\)/gi, '#4f46e5')
                .replace(/lab\([^)]+\)/gi, '#4f46e5')
                .replace(/color\([^)]+\)/gi, '#4f46e5')
                .replace(/hwb\([^)]+\)/gi, '#4f46e5')
                .replace(/lch\([^)]+\)/gi, '#4f46e5');
            }
          });
        }

        // Ensure cloned document body has standard fallback colors
        if (clonedDoc.documentElement) {
          clonedDoc.documentElement.style.backgroundColor = '#ffffff';
          clonedDoc.documentElement.style.color = '#0f172a';
        }
        if (clonedDoc.body) {
          clonedDoc.body.style.backgroundColor = '#ffffff';
          clonedDoc.body.style.color = '#0f172a';
        }

        // Fast native query for only elements that have inline styles with unsupported colors
        const elementsWithInlineColor = Array.from(
          clonedElement.querySelectorAll('[style*="oklch"], [style*="color("], [style*="lab("], [style*="hwb("], [style*="lch("]')
        ) as HTMLElement[];

        elementsWithInlineColor.forEach(el => {
          try {
            const rawStyle = el.getAttribute('style') || '';
            if (/oklch|color\(|lab\(|hwb\(|lch\(/i.test(rawStyle)) {
              const cleanedStyle = rawStyle
                .replace(/oklch\([^)]+\)/gi, '#4f46e5')
                .replace(/lab\([^)]+\)/gi, '#4f46e5')
                .replace(/color\([^)]+\)/gi, '#4f46e5')
                .replace(/hwb\([^)]+\)/gi, '#4f46e5')
                .replace(/lch\([^)]+\)/gi, '#4f46e5');
              el.setAttribute('style', cleanedStyle);
            }
          } catch {
            // ignore individual element error
          }
        });
      } catch (cloneErr) {
        console.warn('safeHtml2Canvas sanitize warning:', cloneErr);
      }

      if (originalOnClone) {
        originalOnClone(clonedDoc, clonedElement);
      }
    }
  };

  const timeoutMs = options.timeout || 15000;
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('html2canvas rendering timed out')), timeoutMs);
  });

  const canvas = await Promise.race([html2canvas(element, safeOptions), timeoutPromise]);

  // Wrap toDataURL with safety fallback if canvas ever happens to be tainted
  try {
    const originalToDataURL = canvas.toDataURL.bind(canvas);
    canvas.toDataURL = function(...args: any[]) {
      try {
        return originalToDataURL(...args);
      } catch (err) {
        console.warn('Canvas toDataURL failed due to tainting, using safe fallback:', err);
        const blankCanvas = document.createElement('canvas');
        blankCanvas.width = canvas.width || 800;
        blankCanvas.height = canvas.height || 1000;
        const bCtx = blankCanvas.getContext('2d');
        if (bCtx) {
          bCtx.fillStyle = '#ffffff';
          bCtx.fillRect(0, 0, blankCanvas.width, blankCanvas.height);
        }
        return blankCanvas.toDataURL('image/png');
      }
    } as any;
  } catch {
    // ignore
  }

  return canvas;
}

export default safeHtml2Canvas;
